import { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MermaidDiagram } from '@/components/ui/MermaidDiagram';

interface Flowchart {
  id: string;
  title: string;
  description: string;
  category: 'lifecycle' | 'workflow' | 'strategy';
  mermaid: string;
}

const flowcharts: Flowchart[] = [
  {
    id: 'deal-lifecycle',
    title: 'Deal Lifecycle',
    description: 'Complete end-to-end flow from sourcing to closing, showing all stages and decision points.',
    category: 'lifecycle',
    mermaid: `flowchart TD
    Start([Start]) --> Source[Deal Sourcing]

    Source --> Score{Score <br/>Opportunity}
    Score -->|Low Fit| Archive1[Archive]
    Score -->|High Fit| LOI[LOI Sent]

    LOI --> LOIResponse{LP <br/>Response?}
    LOIResponse -->|No Response| FollowUp1[Follow-up Sequence]
    FollowUp1 --> LOIResponse
    LOIResponse -->|Rejected| Archive2[Archive]
    LOIResponse -->|Interested| DD[Due Diligence]

    DD --> DDComplete{DD <br/>Complete?}
    DDComplete -->|Issues Found| Renegotiate[Renegotiate Terms]
    Renegotiate --> DDComplete
    DDComplete -->|Failed| Archive3[Archive]
    DDComplete -->|Passed| GPConsent[GP Consent Request]

    GPConsent --> GPResponse{GP <br/>Response?}
    GPResponse -->|Rejected| ForceNotice{Force <br/>Notification?}
    ForceNotice -->|Yes| ReputationRisk[⚠️ Reputation Risk]
    ForceNotice -->|No| Archive4[Archive]
    ReputationRisk --> Documentation
    GPResponse -->|Approved| Documentation[Documentation]

    Documentation --> LegalReview{Legal <br/>Review?}
    LegalReview -->|Issues| ResolveLegal[Resolve Legal Issues]
    ResolveLegal --> LegalReview
    LegalReview -->|Approved| Closing[Closing]

    Closing --> FundAllocation{Fund <br/>Capacity?}
    FundAllocation -->|Bundle for Resale| MultiBundle[Multi-Fund Bundling]
    FundAllocation -->|Hold in Fund| Portfolio[Add to Portfolio]

    MultiBundle --> Closed([Closed - Bundled])
    Portfolio --> Closed2([Closed - Portfolio])

    style Source fill:#e1f5ff
    style LOI fill:#fff4e1
    style DD fill:#ffe1f5
    style GPConsent fill:#ffcccc
    style Documentation fill:#e1ffe1
    style Closing fill:#d4edda
    style Closed fill:#28a745,color:#fff
    style Closed2 fill:#28a745,color:#fff`,
  },
  {
    id: 'gp-consent',
    title: 'GP Consent Workflow',
    description: 'Critical GP interaction process - the highest risk/value step in secondary transactions.',
    category: 'workflow',
    mermaid: `flowchart TD
    Start([DD Passed]) --> Assess[Assess GP Relationship]

    Assess --> GPType{GP <br/>Type?}

    GPType -->|Protective GP| ProtectiveRoute[Collaborative Approach]
    GPType -->|Resistant GP| ResistantRoute[Strategic Approach]

    ProtectiveRoute --> DraftConsent1[Draft Consent Request]
    ResistantRoute --> AnalyzePosition[Analyze Fund Position]

    AnalyzePosition --> BankAnalogy{Like Bank <br/>w/ Bad Loans?}
    BankAnalogy -->|Yes| DiffApproach[Different Strategy Needed]
    BankAnalogy -->|No| DraftConsent2[Draft Consent Request]

    DiffApproach --> ForceOption{Pursue <br/>Force Route?}
    ForceOption -->|No| Archive[Archive Deal]
    ForceOption -->|Yes| RiskAssess[⚠️ Assess Reputation Risk]

    RiskAssess --> ForceNotice[Force LP Notification]

    DraftConsent1 --> SubmitGP[Submit to GP]
    DraftConsent2 --> SubmitGP

    SubmitGP --> GPReview[GP Reviews Request]

    GPReview --> GPDecision{GP <br/>Decision?}

    GPDecision -->|Request Changes| Negotiate[Negotiate Terms]
    Negotiate --> SubmitGP

    GPDecision -->|Rejected| FinalOption{Force <br/>Notice Option?}
    FinalOption -->|Yes| RiskAssess
    FinalOption -->|No| Archive

    GPDecision -->|Approved| LPNotification[GP Notifies LPs]
    ForceNotice --> LPNotification

    LPNotification --> ROFR[ROFR Process]

    ROFR --> ROFRResponse{Other LPs <br/>Exercise ROFR?}
    ROFRResponse -->|Yes| Compete[Compete or Withdraw]
    ROFRResponse -->|No| Proceed[Proceed to Documentation]

    Compete --> CompeteDecision{Continue?}
    CompeteDecision -->|No| Archive
    CompeteDecision -->|Yes| Proceed

    Proceed --> Success([GP Consent Obtained])

    style Start fill:#e1f5ff
    style ProtectiveRoute fill:#d4edda
    style ResistantRoute fill:#fff3cd
    style RiskAssess fill:#dc3545,color:#fff
    style ForceNotice fill:#dc3545,color:#fff
    style Success fill:#28a745,color:#fff
    style Archive fill:#f8d7da`,
  },
  {
    id: 'deal-sourcing',
    title: 'Deal Sourcing',
    description: 'Multi-channel sourcing strategy with SEC EDGAR as primary engine.',
    category: 'workflow',
    mermaid: `flowchart TD
    Start([Sourcing Engine]) --> Channels{Sourcing <br/>Channels}

    Channels --> SEC[SEC EDGAR Filings]
    Channels --> Broker[Broker Teasers]
    Channels --> Direct[LP Direct Outreach]
    Channels --> Distress[Distress Signals]

    SEC --> Query1[Query 10+ Year Old Funds]
    Query1 --> Filter1[Filter Remaining Positions]
    Filter1 --> Extract1[Extract Portfolio Companies]
    Extract1 --> Remaining[Identify Opportunities]

    Broker --> Review[Review Teaser Materials]
    Review --> QualifyBroker{Meets <br/>Thesis?}
    QualifyBroker -->|No| Archive1[Archive]
    QualifyBroker -->|Yes| ContactBroker[Contact Broker]

    Direct --> TargetLP[Target Specific LPs]
    TargetLP --> Outreach[Cold Outreach Campaign]
    Outreach --> Response{Response?}
    Response -->|No| FollowUp[Follow-up Sequence]
    FollowUp --> Response
    Response -->|Yes| Qualify

    Distress --> Monitor[Monitor LP Forums]
    Monitor --> Signals[Identify Liquidity Needs]
    Signals --> DirectContact[Direct Contact]

    Remaining --> Enrich[Enrich with News/Data]
    ContactBroker --> Enrich
    DirectContact --> Enrich

    Enrich --> WebSearch[Free Web Search APIs]
    WebSearch --> NewsAnalysis[News Analysis]
    NewsAnalysis --> Qualify{Qualifies?}

    Qualify -->|No| Archive2[Archive]
    Qualify -->|Yes| Score[Score Opportunity]

    Score --> Thesis[Score by Thesis Fit]
    Thesis --> Valuation[Initial Valuation]
    Valuation --> Priority[Set Priority Level]

    Priority --> Dashboard[Add to Dashboard]
    Dashboard --> Template[Generate Outreach]
    Template --> Ready([Ready for LOI])

    style SEC fill:#e1f5ff
    style Broker fill:#fff4e1
    style Direct fill:#ffe1f5
    style Distress fill:#ffcccc
    style Dashboard fill:#d4edda
    style Ready fill:#28a745,color:#fff`,
  },
  {
    id: 'multi-fund-bundling',
    title: 'Multi-Fund Bundling',
    description: 'Strategy for bundling 3-5 acquired fund positions for resale - "a whole new asset class".',
    category: 'strategy',
    mermaid: `flowchart TD
    Start([Acquired Positions]) --> Monitor[Monitor Portfolio]

    Monitor --> Inventory{Current <br/>Inventory}
    Inventory --> Count[Count Available Positions]

    Count --> Sufficient{3-5 Positions <br/>Available?}
    Sufficient -->|No| WaitMore[Wait for More Deals]
    WaitMore --> Monitor

    Sufficient -->|Yes| Analyze[Analyze Bundle Potential]

    Analyze --> Criteria[Evaluate Bundle Criteria]
    Criteria --> Theme{Bundle <br/>Theme?}

    Theme --> SectorBundle[Sector-Focused]
    Theme --> StageBundle[Stage-Focused]
    Theme --> VintageBundle[Vintage-Focused]
    Theme --> MixedBundle[Mixed Strategy]

    SectorBundle --> Select[Select 3-5 Positions]
    StageBundle --> Select
    VintageBundle --> Select
    MixedBundle --> Select

    Select --> Model[Build Bundle Valuation]
    Model --> FeeCalc[Calculate Aggregate Fee Drag]
    FeeCalc --> Discount[Determine Bundle Discount]

    Discount --> Markup[Calculate Target Markup]
    Markup --> TargetBuyers[Identify Target Buyers]

    TargetBuyers --> BuyerType{Buyer <br/>Type?}

    BuyerType --> Institution[Institutional Investors]
    BuyerType --> FamilyOffice[Family Offices]
    BuyerType --> SecondaryFund[Other Secondary Funds]

    Institution --> Pitch[Create Bundle Pitch Deck]
    FamilyOffice --> Pitch
    SecondaryFund --> Pitch

    Pitch --> Market[Market Bundle]
    Market --> Interest{Buyer <br/>Interest?}

    Interest -->|No| Adjust{Adjust <br/>Terms?}
    Adjust -->|Yes| Discount
    Adjust -->|No| Unbundle[Unbundle & Hold]

    Interest -->|Yes| Negotiate[Negotiate Terms]
    Negotiate --> DD[Buyer Due Diligence]

    DD --> DDPass{DD <br/>Passed?}
    DDPass -->|No| ResolveIssues[Resolve Issues]
    ResolveIssues --> DD
    DDPass -->|Yes| Legal[Legal Documentation]

    Legal --> MultiGP[Coordinate Multiple GPs]
    MultiGP --> GPApprovals{All GP <br/>Approvals?}
    GPApprovals -->|No| RiskMitigation[Risk Mitigation]
    RiskMitigation --> GPApprovals
    GPApprovals -->|Yes| Close[Close Bundle Sale]

    Close --> Distribution[Distribute Proceeds]
    Distribution --> Returns[Calculate Returns]
    Returns --> Report[Report to Fund LPs]

    Report --> Success([Bundle Sold])
    Unbundle --> Portfolio([Hold in Portfolio])

    style Start fill:#e1f5ff
    style Select fill:#fff4e1
    style Model fill:#ffe1f5
    style Pitch fill:#d4edda
    style Close fill:#28a745,color:#fff
    style Success fill:#28a745,color:#fff
    style MultiGP fill:#ffcccc`,
  },
  {
    id: 'portfolio-valuation',
    title: 'Portfolio Valuation',
    description: 'Calculate TRUE discount with fee drag analysis - your core methodology.',
    category: 'workflow',
    mermaid: `flowchart TD
    Start([Deal Identified]) --> Extract[Extract Fund Financials]

    Extract --> Sources{Data <br/>Sources}
    Sources --> SECDocs[SEC Filings]
    Sources --> LPReports[LP Reports]
    Sources --> PublicData[Public Company Data]

    SECDocs --> Parse[Parse Financial Data]
    LPReports --> Parse
    PublicData --> Parse

    Parse --> Components[Identify Components]
    Components --> ManagementFees[Management Fees]
    Components --> CarriedInterest[Carried Interest]
    Components --> OtherFees[Other Fees/Expenses]

    ManagementFees --> Timeline[Calculate Over Timeline]
    CarriedInterest --> Timeline
    OtherFees --> Timeline

    Timeline --> Years[10+ Years of Fees]
    Years --> Compound[Compound Fee Drag Effect]

    Compound --> Distributions[Analyze Distributions]
    Distributions --> DistCheck{Distributions <br/>Made?}

    DistCheck -->|Yes| CalcRemaining[Calculate Remaining Value]
    DistCheck -->|No| FullValue[Full Original Investment]

    CalcRemaining --> Portfolio[Remaining Portfolio Value]
    FullValue --> Portfolio

    Portfolio --> Companies[Value Portfolio Companies]
    Companies --> Method{Valuation <br/>Method}

    Method --> LastRound[Last Round Valuation]
    Method --> Comparable[Comparable Analysis]
    Method --> NewsSignals[News/Signal Analysis]

    LastRound --> AdjustAge[Adjust for Age/Stagnation]
    Comparable --> AdjustAge
    NewsSignals --> AdjustAge

    AdjustAge --> Aggregate[Aggregate Portfolio Value]
    Aggregate --> FeeDrag[Subtract Fee Drag]

    FeeDrag --> TrueValue[TRUE Current Value]
    TrueValue --> Discount[Calculate Discount Range]

    Discount --> MinDiscount[Minimum: 30%]
    Discount --> MaxDiscount[Maximum: 70%]

    MinDiscount --> Factors{Adjust for <br/>Factors}
    MaxDiscount --> Factors

    Factors --> OfferRange[Determine Offer Range]
    OfferRange --> TargetOffer[Calculate Target Offer]
    TargetOffer --> Upside[Calculate Upside Potential]

    Upside --> Exit{Exit <br/>Scenarios}
    Exit --> BaseCase[Base Case: 2-3x]
    Exit --> BullCase[Bull Case: 5-10x]
    Exit --> BearCase[Bear Case: 0.5-1x]

    BaseCase --> ROI[Calculate Expected ROI]
    BullCase --> ROI
    BearCase --> ROI

    ROI --> Decision{Meets <br/>Threshold?}
    Decision -->|No| Reject[Reject Deal]
    Decision -->|Yes| Generate[Generate Offer Terms]

    Generate --> Ready([Ready for LOI])
    Reject --> Archive([Archive])

    style Extract fill:#e1f5ff
    style FeeDrag fill:#ffcccc
    style TrueValue fill:#d4edda
    style OfferRange fill:#fff4e1
    style Ready fill:#28a745,color:#fff
    style Archive fill:#f8d7da`,
  },
  {
    id: 'dashboard-prioritization',
    title: 'Deal Prioritization Logic',
    description: 'How deals are color-coded and prioritized in the dashboard.',
    category: 'strategy',
    mermaid: `flowchart TD
    Start([Deal Enters Pipeline]) --> Assess[Assess Deal Metrics]

    Assess --> Probability[Calculate Close Probability]
    Assess --> DollarSize[Calculate Dollar Size]
    Assess --> Timeline[Estimate Timeline]
    Assess --> Concerns[Identify Concerns]

    Probability --> ProbScore{Probability?}
    ProbScore -->|>70%| HighProb[🟢 High]
    ProbScore -->|40-70%| MedProb[🟡 Medium]
    ProbScore -->|<40%| LowProb[🔴 Low]

    DollarSize --> SizeScore{Dollar Size?}
    SizeScore -->|>$1M| Large[🟢 Large Deal]
    SizeScore -->|$250K-$1M| Medium[🟡 Medium Deal]
    SizeScore -->|<$250K| Small[🔴 Small Deal]

    Timeline --> TimeScore{Timeline?}
    TimeScore -->|<30 days| Fast[🟢 Fast Close]
    TimeScore -->|30-90 days| Normal[🟡 Normal]
    TimeScore -->|>90 days| Slow[🔴 Slow Deal]

    Concerns --> Issues{Concerns?}
    Issues -->|GP Resistance| Red[🔴 Red Flag]
    Issues -->|Legal Complexity| Yellow[🟡 Yellow Flag]
    Issues -->|None| Green[🟢 Green Flag]

    HighProb --> Priority[Calculate Priority Score]
    MedProb --> Priority
    LowProb --> Priority
    Large --> Priority
    Medium --> Priority
    Small --> Priority
    Fast --> Priority
    Normal --> Priority
    Slow --> Priority
    Red --> Priority
    Yellow --> Priority
    Green --> Priority

    Priority --> Capacity{Fund Capacity?}
    Capacity --> Available[Check Available Capital]

    Available --> Allocated{Allocation Status?}
    Allocated -->|Over-allocated| ReduceOffer[Reduce Offer]
    Allocated -->|Under-allocated| IncreaseOffer[Increase Offer]
    Allocated -->|Optimal| Maintain[Maintain Offer]

    ReduceOffer --> Display[Update Dashboard]
    IncreaseOffer --> Display
    Maintain --> Display

    Display --> ColorCode{Final Color}
    ColorCode -->|High Priority| GreenDisplay[🟢 GREEN ROW]
    ColorCode -->|Medium Priority| YellowDisplay[🟡 YELLOW ROW]
    ColorCode -->|Low Priority| RedDisplay[🔴 RED ROW]

    GreenDisplay --> Dashboard([Dashboard View])
    YellowDisplay --> Dashboard
    RedDisplay --> Dashboard

    style HighProb fill:#d4edda
    style Large fill:#d4edda
    style Fast fill:#d4edda
    style Green fill:#d4edda
    style GreenDisplay fill:#28a745,color:#fff
    style MedProb fill:#fff3cd
    style Medium fill:#fff3cd
    style Normal fill:#fff3cd
    style Yellow fill:#fff3cd
    style YellowDisplay fill:#ffc107
    style LowProb fill:#f8d7da
    style Small fill:#f8d7da
    style Slow fill:#f8d7da
    style Red fill:#dc3545,color:#fff
    style RedDisplay fill:#dc3545,color:#fff`,
  },
];

const categoryLabels = {
  lifecycle: 'Deal Lifecycle',
  workflow: 'Core Workflows',
  strategy: 'Strategy & Logic',
};

const categoryColors = {
  lifecycle: 'bg-blue-100 text-blue-800 border-blue-200',
  workflow: 'bg-green-100 text-green-800 border-green-200',
  strategy: 'bg-purple-100 text-purple-800 border-purple-200',
};

export function FlowchartsPage(): React.ReactElement {
  const [expandedChart, setExpandedChart] = useState<string | null>('deal-lifecycle');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filteredCharts = selectedCategory === 'all'
    ? flowcharts
    : flowcharts.filter(f => f.category === selectedCategory);

  // Function to copy Mermaid code to clipboard
  const copyToClipboard = async (chartId: string, mermaidCode: string) => {
    try {
      await navigator.clipboard.writeText(mermaidCode);
      setCopiedId(chartId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Function to render Mermaid diagram in Mermaid Live Editor
  // Uses pako compression for reliable encoding of complex diagrams
  const openInMermaidLive = (mermaid: string) => {
    try {
      // Method 1: Use the simpler URL encoding for basic diagrams
      // Mermaid Live also supports direct pako/base64 format
      const state = {
        code: mermaid,
        mermaid: { theme: 'default' },
        autoSync: true,
        updateDiagram: true,
      };

      // Convert to base64 with proper Unicode handling
      const jsonString = JSON.stringify(state);
      const utf8Bytes = new TextEncoder().encode(jsonString);
      const base64 = btoa(String.fromCharCode(...utf8Bytes));

      // Use the pako format that Mermaid Live expects
      window.open(`https://mermaid.live/edit#pako:${base64}`, '_blank');
    } catch (e) {
      // Fallback: Open Mermaid Live and let user paste manually
      console.error('Failed to encode diagram:', e);
      navigator.clipboard.writeText(mermaid);
      alert('Diagram code copied to clipboard! Opening Mermaid Live - paste the code there.');
      window.open('https://mermaid.live/edit', '_blank');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Flowcharts</h2>
          <p className="text-gray-600">Visual workflows for secondary market deal management</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Filter:</span>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Flowcharts</option>
            <option value="lifecycle">Deal Lifecycle</option>
            <option value="workflow">Core Workflows</option>
            <option value="strategy">Strategy & Logic</option>
          </select>
        </div>
      </div>

      {/* Quick Overview */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="font-medium text-gray-900 mb-1">🎯 GP Consent is Critical</p>
            <p className="text-gray-600">Two GP types: Protective (partners) vs Resistant ("banks with bad loans")</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="font-medium text-gray-900 mb-1">📦 Multi-Fund Bundling</p>
            <p className="text-gray-600">Bundle 3-5 positions for resale = "whole new asset class"</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="font-medium text-gray-900 mb-1">📊 TRUE Discount</p>
            <p className="text-gray-600">Headline discount minus fee drag = real opportunity value</p>
          </div>
        </div>
      </div>

      {/* Flowchart Cards */}
      <div className="space-y-4">
        {filteredCharts.map((chart) => {
          const isExpanded = expandedChart === chart.id;

          return (
            <div
              key={chart.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            >
              {/* Header */}
              <div
                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedChart(isExpanded ? null : chart.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <span className={cn(
                      'px-3 py-1 text-xs font-medium rounded-full border flex-shrink-0',
                      categoryColors[chart.category]
                    )}>
                      {categoryLabels[chart.category]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900">{chart.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{chart.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                  {/* Action Buttons */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
                    <p className="text-sm text-gray-600">
                      Interactive diagram rendered inline
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyToClipboard(chart.id, chart.mermaid)}
                        className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
                        title="Copy Mermaid code"
                      >
                        <Copy className="h-4 w-4" />
                        {copiedId === chart.id ? 'Copied!' : 'Copy Code'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openInMermaidLive(chart.mermaid);
                        }}
                        className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
                        title="Open in Mermaid Live Editor"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open in Editor
                      </button>
                    </div>
                  </div>

                  {/* Embedded Mermaid Diagram */}
                  <div className="p-6">
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                      <MermaidDiagram
                        chart={chart.mermaid}
                        id={`diagram-${chart.id}`}
                        className="min-h-[400px]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Usage Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg
            className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm text-blue-900">
              <strong>Interactive Diagrams:</strong> Click any flowchart to expand and view the embedded diagram.
              Use "Copy Code" to export the Mermaid syntax, or "Open in Editor" to modify in Mermaid Live.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
